import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";

type SemanticModelStatus = "active" | "draft" | "inactive";

type SemanticModelListItem = {
  id: string;
  name: string;
  description: string | null;
  status: SemanticModelStatus;
  tags: string[];
  object_count: number;
  subject_area_count: number;
  owner_email: string | null;
  updated_at: string;
  created_at: string;
};

export function SemanticModels() {
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<SemanticModelListItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      const suffix = qs.toString();
      const data = await apiFetch<{ semantic_models: SemanticModelListItem[] }>(
        `/api/tenant/semantic-models${suffix.length > 0 ? `?${suffix}` : ""}`
      );
      setRows(data.semantic_models);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [statusFilter]);

  async function createModel() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const data = await apiFetch<{ semantic_model: { id: string } }>("/api/tenant/semantic-models", {
        method: "POST",
        body: { name: name.trim(), description: description.trim() || null }
      });
      setCreateOpen(false);
      setName("");
      setDescription("");
      nav(`/app/build/semantic-models/${encodeURIComponent(data.semantic_model.id)}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  async function duplicateModel(row: SemanticModelListItem) {
    try {
      const data = await apiFetch<{ semantic_model: { id: string } }>(
        `/api/tenant/semantic-models/${encodeURIComponent(row.id)}/duplicate`,
        { method: "POST", body: {} }
      );
      await load();
      nav(`/app/build/semantic-models/${encodeURIComponent(data.semantic_model.id)}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function deleteModel(row: SemanticModelListItem) {
    const ok = await confirm.confirm({
      title: "Delete semantic model?",
      detail: `This deletes "${row.name}" and cannot be undone.`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/semantic-models/${encodeURIComponent(row.id)}`, { method: "DELETE" });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <PageLayout
      title="Semantic Models"
      actions={
        <button className="adminNewLink" type="button" onClick={() => setCreateOpen(true)}>
          + New model
        </button>
      }
    >
      <div className="card adminList">
        <div className="field" style={{ marginBottom: 12, maxWidth: 280 }}>
          <label className="label" htmlFor="semantic-status-filter">
            Status
          </label>
          <select
            id="semantic-status-filter"
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {loading ? <LoadingState /> : null}
        {!loading && error ? <ErrorState detail={error} /> : null}
        {!loading && !error && rows.length === 0 ? <div className="adminListEmpty">No semantic models</div> : null}
        {!loading && !error && rows.length > 0 ? (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Objects</th>
                  <th>Subject areas</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <button
                        className="adminListName"
                        type="button"
                        onClick={() => nav(`/app/build/semantic-models/${row.id}`)}
                      >
                        {row.name}
                      </button>
                    </td>
                    <td>{row.status}</td>
                    <td>{row.object_count}</td>
                    <td>{row.subject_area_count}</td>
                    <td>{new Date(row.updated_at).toLocaleString()}</td>
                    <td>
                      <div className="adminListActions">
                        <button className="adminListAction" type="button" onClick={() => nav(`/app/build/semantic-models/${row.id}`)}>
                          Edit
                        </button>
                        <button className="adminListAction" type="button" onClick={() => void duplicateModel(row)}>
                          Duplicate
                        </button>
                        <button className="adminListAction isDanger" type="button" onClick={() => void deleteModel(row)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {createOpen ? (
        <div className="modalOverlay" role="presentation" onClick={() => setCreateOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="reportModalTitle">New semantic model</div>
            <div className="field">
              <label className="label" htmlFor="sem-name">
                Name
              </label>
              <input id="sem-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label className="label" htmlFor="sem-description">
                Description
              </label>
              <textarea
                id="sem-description"
                className="input reportTextarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="actions">
              <button className="btn" type="button" onClick={() => setCreateOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btnPrimary" type="button" onClick={() => void createModel()} disabled={saving || !name.trim()}>
                Create
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </PageLayout>
  );
}
